import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, Spin } from "antd";
import axiosInstance from "api/axiosInstance";
import { AuthPageContainer } from "components/UI/AuthPageContainer/AuthPageContainer.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useSession } from "hooks/useSession";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/$tenantSlug/auth/_layout/accept-invitation/$id/$hash")({
    component: AcceptInvitationPage,
    validateSearch: (search: Record<string, unknown>) => ({
        expires: String(search.expires ?? ""),
        signature: String(search.signature ?? ""),
    }),
});

function AcceptInvitationPage() {
    const { tenantSlug, id, hash } = Route.useParams();
    const { expires, signature } = Route.useSearch();
    const navigate = useNavigate();
    const { t } = usePaldockTranslation("pages.accept-invitation");
    const sessionUtils = useSession();

    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        axiosInstance
            .post(`/${tenantSlug}/user-requests/${id}/${hash}/accept`, null, {
                params: { expires, signature },
            })
            .then(() => {
                setStatus("success");
                setTimeout(() => {
                    if (sessionUtils.getTokenFromStorage()) {
                        navigate({ to: "/$tenantSlug", params: { tenantSlug }, replace: true });
                    } else {
                        navigate({ to: "/$tenantSlug/auth/login", params: { tenantSlug }, replace: true });
                    }
                }, 2000);
            })
            .catch((err) => {
                setStatus("error");
                setErrorMessage(err?.response?.data?.message ?? t("error.generic"));
            });
    }, []);

    return (
        <AuthPageContainer>
            <div className="flex flex-col items-center gap-4 w-full">
                {status === "loading" && (
                    <div className="flex flex-col items-center gap-3 py-4">
                        <Spin size="large" />
                        <p className="text-gray-500 m-0">{t("loading")}</p>
                    </div>
                )}
                {status === "success" && (
                    <Alert
                        type="success"
                        message={t("success.title")}
                        description={t("success.redirecting")}
                        showIcon
                        className="w-full"
                        style={{ boxShadow: "0px 3px 6px -4px #0000001F", border: "none" }}
                    />
                )}
                {status === "error" && (
                    <Alert
                        type="error"
                        message={t("error.title")}
                        description={errorMessage}
                        showIcon
                        className="w-full"
                        style={{ boxShadow: "0px 3px 6px -4px #0000001F", border: "none" }}
                    />
                )}
            </div>
        </AuthPageContainer>
    );
}
